import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Phone, Mail, Users, FileText, CheckSquare, Check, Edit2, Trash2 } from 'lucide-react';
import { crmActivitiesApi } from '../../api/crm';
import { useConfirm } from '../ConfirmDialog';
import type { CrmActivity, CrmActivityType } from '../../types';
import CrmActivityForm from './CrmActivityForm';

const TYPE_ICONS: Record<CrmActivityType, React.ReactNode> = {
  call:    <Phone className="w-3.5 h-3.5" />,
  email:   <Mail className="w-3.5 h-3.5" />,
  meeting: <Users className="w-3.5 h-3.5" />,
  note:    <FileText className="w-3.5 h-3.5" />,
  task:    <CheckSquare className="w-3.5 h-3.5" />,
};

// Domain category colors mapped onto semantic tokens (text + tinted tile).
const TYPE_COLOR: Record<CrmActivityType, string> = {
  call:    'var(--scpa-primary)',
  email:   'var(--scpa-accent)',
  meeting: 'var(--scpa-status-warning)',
  note:    'var(--scpa-text-muted)',
  task:    'var(--scpa-status-ok)',
};

interface Props {
  activity: CrmActivity;
  onUpdated: () => void;
}

export default function CrmActivityItem({ activity, onUpdated }: Props) {
  const [editing, setEditing] = useState(false);
  const confirm = useConfirm();

  const completeMutation = useMutation({
    mutationFn: () => crmActivitiesApi.complete(activity.id),
    onSuccess: onUpdated,
  });

  const deleteMutation = useMutation({
    mutationFn: () => crmActivitiesApi.delete(activity.id),
    onSuccess: onUpdated,
  });

  const handleDelete = async () => {
    if (await confirm({ title: 'Delete activity', message: 'Delete this activity? This cannot be undone.', confirmLabel: 'Delete', danger: true })) {
      deleteMutation.mutate();
    }
  };

  const isOverdue = activity.dueAt && new Date(activity.dueAt) < new Date() && activity.status !== 'completed';
  const tint = TYPE_COLOR[activity.type];

  return (
    <>
      <div className={`flex items-start gap-3 p-3 rounded-lg border border-line bg-surface ${activity.status === 'completed' ? 'opacity-60' : ''}`}>
        <div
          className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ color: tint, backgroundColor: 'color-mix(in srgb, ' + tint + ' 14%, transparent)' }}
        >
          {TYPE_ICONS[activity.type]}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium text-fg ${activity.status === 'completed' ? 'line-through' : ''}`}>{activity.subject}</p>
          {activity.description && <p className="text-xs text-muted mt-0.5 line-clamp-2">{activity.description}</p>}
          {activity.outcome && <p className="text-xs text-muted mt-1 italic">Outcome: {activity.outcome}</p>}
          <div className="flex items-center gap-3 mt-1 text-xs text-muted">
            <span className="capitalize">{activity.type}</span>
            {activity.dueAt && (
              <span className={isOverdue ? 'text-err font-medium' : ''}>
                {isOverdue ? 'Overdue · ' : ''}{new Date(activity.dueAt).toLocaleDateString()}
              </span>
            )}
            {activity.durationMinutes && <span>{activity.durationMinutes}m</span>}
            {activity.owner && <span>· {activity.owner.name}</span>}
            {activity.completedAt && <span>· Completed {new Date(activity.completedAt).toLocaleDateString()}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {activity.status !== 'completed' && (
            <button
              onClick={() => completeMutation.mutate()}
              className="p-1.5 rounded-md text-muted hover:text-ok hover:bg-row-odd transition-colors"
              title="Mark complete"
            >
              <Check className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={() => setEditing(true)} className="p-1.5 rounded-md text-muted hover:text-primary hover:bg-row-odd transition-colors">
            <Edit2 className="w-3 h-3" />
          </button>
          <button onClick={handleDelete} className="p-1.5 rounded-md text-muted hover:text-err hover:bg-row-odd transition-colors">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>

      {editing && (
        <CrmActivityForm
          activity={activity}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); onUpdated(); }}
        />
      )}
    </>
  );
}
